"use client";

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useStore } from '@/context/StoreContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faRotateLeft, faTruckFast, faShieldHalved, faArrowLeft, 
    faCheckCircle, faClock, faCircleInfo, faLock
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

function PoliticasContent() {
    const searchParams = useSearchParams();
    const tabParam = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState<'trocas' | 'entregas' | 'privacidade'>('trocas');
    const { settings } = useStore();

    useEffect(() => {
        if (tabParam === 'entregas' || tabParam === 'privacidade' || tabParam === 'trocas') {
            setActiveTab(tabParam);
        }
    }, [tabParam]);

    const storeName = settings.store_name || "Nossa Loja";
    const contactEmail = settings.email || "atendimento@loja.com";
    const contactPhone = settings.whatsapp_widget?.phone || settings.phone || "";
    const cleanPhone = contactPhone.replace(/\D/g, '');
    const cnpj = settings.cnpj || "";

    const hasAddress = settings.street || settings.city;
    const formattedAddress = [
        settings.street ? `${settings.street}${settings.number ? ', ' + settings.number : ''}` : '',
        settings.complement,
        settings.district,
        settings.city && settings.state ? `${settings.city}/${settings.state}` : (settings.city || settings.state),
        settings.cep ? `CEP: ${settings.cep}` : ''
    ].filter(Boolean).join(' - ');

    return (
        <div className="bg-[var(--page-bg,#fff8f6)] min-h-screen flex flex-col">
            <Header />

            <main className="flex-grow container mx-auto px-4 py-8 max-w-5xl">
                {/* Voltar */}
                <div className="mb-6">
                    <Link 
                        href="/" 
                        className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-[var(--primary)] transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="w-3.5 h-3.5" />
                        Voltar para a Loja
                    </Link>
                </div>

                {/* Hero Header */}
                <div className="text-center mb-8">
                    <span className="text-xs font-black tracking-widest uppercase text-[var(--primary)]">
                        TRANSPARÊNCIA & COMPROMISSO
                    </span>
                    <h1 className="text-3xl md:text-4xl font-black text-gray-900 mt-1">
                        Políticas Comerciais & Legais
                    </h1>
                    <p className="text-sm text-gray-500 mt-2 max-w-xl mx-auto">
                        Conheça os termos de compra, entrega, privacidade e garantia da {storeName}, em total conformidade com o Código de Defesa do Consumidor e a LGPD.
                    </p>
                </div>

                {/* Tab Switcher */}
                <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-8">
                    <button
                        onClick={() => setActiveTab('trocas')}
                        className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs md:text-sm font-bold transition-all shadow-xs ${
                            activeTab === 'trocas'
                                ? 'bg-[var(--primary)] text-white shadow-md scale-105'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                        }`}
                    >
                        <FontAwesomeIcon icon={faRotateLeft} />
                        Trocas & Devoluções
                    </button>

                    <button
                        onClick={() => setActiveTab('entregas')}
                        className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs md:text-sm font-bold transition-all shadow-xs ${
                            activeTab === 'entregas'
                                ? 'bg-[var(--primary)] text-white shadow-md scale-105'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                        }`}
                    >
                        <FontAwesomeIcon icon={faTruckFast} />
                        Prazos & Entregas
                    </button>

                    <button
                        onClick={() => setActiveTab('privacidade')}
                        className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs md:text-sm font-bold transition-all shadow-xs ${
                            activeTab === 'privacidade'
                                ? 'bg-[var(--primary)] text-white shadow-md scale-105'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                        }`}
                    >
                        <FontAwesomeIcon icon={faShieldHalved} />
                        Privacidade & LGPD
                    </button>
                </div>

                {/* Conteúdo das Abas */}
                <div className="bg-white rounded-3xl p-6 md:p-10 border border-gray-100 shadow-sm">
                    {/* ABA 1: TROCAS & DEVOLUÇÕES */}
                    {activeTab === 'trocas' && (
                        <div className="space-y-6 text-gray-700 leading-relaxed text-sm">
                            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                                <div className="w-10 h-10 rounded-2xl bg-orange-50 text-[var(--primary)] flex items-center justify-center text-lg flex-shrink-0">
                                    <FontAwesomeIcon icon={faRotateLeft} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Política de Trocas e Devoluções</h2>
                                    <p className="text-xs text-gray-400">Garantia total de satisfação e respeito aos seus direitos</p>
                                </div>
                            </div>

                            <div className="p-4 rounded-2xl bg-amber-50/70 border border-amber-200/60 flex items-start gap-3">
                                <FontAwesomeIcon icon={faCircleInfo} className="text-amber-600 mt-1 flex-shrink-0" />
                                <div className="text-xs text-amber-900">
                                    <strong>Direito de Arrependimento (Art. 49 do CDC):</strong> Você tem até <strong>7 (sete) dias corridos</strong> após o recebimento do produto para solicitar a devolução ou troca por desistência, com reembolso integral incluindo o valor do frete.
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">1. Condições para Devolução ou Troca</h3>
                                <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
                                    <li>O produto deve estar na embalagem original, acompanhado de todos os acessórios e manuais.</li>
                                    <li>Não deve conter indícios de mau uso, avaria física ou violação de lacres do fabricante.</li>
                                    <li>Em caso de produtos perecíveis, alimentos ou suplementos abertos, por questões sanitárias a devolução só será aceita em caso de vício de fabricação ou divergência do item entregue.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">2. Como Solicitar</h3>
                                <p className="text-gray-600">
                                    Para iniciar o processo de troca ou devolução, entre em contato com nossa equipe informando o <strong>número do pedido</strong> e fotos do item:
                                </p>
                                <div className="mt-3 flex flex-wrap gap-4">
                                    {contactPhone && (
                                        <a 
                                            href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(`Olá! Gostaria de solicitar troca/devolução de um pedido na ${storeName}.`)}`}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-4 py-2.5 rounded-full transition-all"
                                        >
                                            <FontAwesomeIcon icon={faWhatsapp} className="text-base" />
                                            WhatsApp: {contactPhone}
                                        </a>
                                    )}
                                    {contactEmail && (
                                        <a 
                                            href={`mailto:${contactEmail}`}
                                            className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs px-4 py-2.5 rounded-full transition-all"
                                        >
                                            E-mail: {contactEmail}
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">3. Prazos e Formas de Reembolso</h3>
                                <p className="text-gray-600">
                                    Após a chegada do produto ao nosso centro de distribuição e validação das condições:
                                </p>
                                <ul className="list-disc pl-5 space-y-1.5 text-gray-600 mt-2">
                                    <li><strong>Pix:</strong> Reembolso realizado na mesma chave de origem em até 2 dias úteis.</li>
                                    <li><strong>Cartão de Crédito:</strong> O estorno é solicitado à operadora em até 3 dias úteis e constará em até 2 faturas subsequentes.</li>
                                    <li><strong>Vale-Compras:</strong> Liberado imediatamente com validade de 12 meses na loja virtual.</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* ABA 2: PRAZOS & ENTREGAS */}
                    {activeTab === 'entregas' && (
                        <div className="space-y-6 text-gray-700 leading-relaxed text-sm">
                            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                                <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-lg flex-shrink-0">
                                    <FontAwesomeIcon icon={faTruckFast} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Prazos e Métodos de Entrega</h2>
                                    <p className="text-xs text-gray-400">Agilidade e rastreamento em tempo real do seu pedido</p>
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">1. Cálculo do Frete e Prazo</h3>
                                <p className="text-gray-600">
                                    O prazo de entrega e o valor do frete são calculados automaticamente no carrinho de compras com base no CEP de destino, peso total dos itens e modalidade de envio escolhida (Correios PAC/Sedex ou Transportadora parceira).
                                </p>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">2. Despacho e Rastreamento</h3>
                                <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
                                    <li>Pedidos aprovados em dias úteis são postados em até <strong>24 a 48 horas</strong>.</li>
                                    <li>Você receberá atualizações no seu WhatsApp ou e-mail com o código de rastreamento assim que o pacote for expedido.</li>
                                    <li>Você também pode acompanhar seu pedido a qualquer momento na página pública <strong>Meus Pedidos</strong> informando o ID do pedido.</li>
                                </ul>
                            </div>

                            {hasAddress && (
                                <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200">
                                    <h4 className="font-bold text-gray-900 text-sm mb-1 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                                        Retirada Presencial na Loja Física
                                    </h4>
                                    <p className="text-xs text-gray-600">
                                        Disponível para clientes da região. Você será notificado assim que o pedido estiver separado no balcão:
                                    </p>
                                    <p className="text-xs font-semibold text-gray-800 mt-1">{formattedAddress}</p>
                                </div>
                            )}

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">3. Tentativas de Entrega</h3>
                                <p className="text-gray-600">
                                    As transportadoras e Correios realizam até 3 (três) tentativas de entrega no endereço indicado. Caso não haja ninguém para receber, o pacote poderá ser retirado na agência mais próxima ou retornará ao nosso centro de distribuição.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ABA 3: PRIVACIDADE & LGPD */}
                    {activeTab === 'privacidade' && (
                        <div className="space-y-6 text-gray-700 leading-relaxed text-sm">
                            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg flex-shrink-0">
                                    <FontAwesomeIcon icon={faShieldHalved} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Política de Privacidade & Proteção de Dados (LGPD)</h2>
                                    <p className="text-xs text-gray-400">Segurança de dados regulamentada pela Lei Federal nº 13.709/2018</p>
                                </div>
                            </div>

                            <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200/60 flex items-start gap-3">
                                <FontAwesomeIcon icon={faLock} className="text-emerald-600 mt-1 flex-shrink-0" />
                                <div className="text-xs text-emerald-950">
                                    <strong>Compromisso de Segurança:</strong> Seus dados pessoais e informações de pagamento são criptografados com certificado SSL (HTTPS) de ponta a ponta. Não armazenamos senhas de cartão nem vendemos suas informações para terceiros.
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">1. Dados Coletados e Finalidade</h3>
                                <p className="text-gray-600">
                                    Coletamos exclusivamente os dados necessários para a execução do contrato de compra e venda:
                                </p>
                                <ul className="list-disc pl-5 space-y-1.5 text-gray-600 mt-2">
                                    <li><strong>Nome e CPF/CNPJ:</strong> Emissão da Nota Fiscal Eletrônica e cumprimento de obrigações tributárias legais.</li>
                                    <li><strong>Endereço de Entrega:</strong> Cálculo de frete e envio físico do pedido através de transportadoras parceiras.</li>
                                    <li><strong>Telefone e WhatsApp:</strong> Notificações de confirmação de pedido, código de rastreio e suporte pós-venda.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-900 text-base mb-2">2. Seus Direitos como Titular de Dados</h3>
                                <p className="text-gray-600">
                                    Conforme o Art. 18 da LGPD, você pode a qualquer momento solicitar:
                                </p>
                                <ul className="list-disc pl-5 space-y-1.5 text-gray-600 mt-2">
                                    <li>Confirmação da existência de tratamento e acesso aos dados.</li>
                                    <li>Correção de dados incompletos ou inexatos.</li>
                                    <li>Anonimização, bloqueio ou eliminação de dados desnecessários.</li>
                                </ul>
                                <p className="text-gray-600 mt-2">
                                    Para exercer seus direitos, basta enviar uma mensagem para nosso canal de atendimento: <span className="font-semibold text-gray-900">{contactEmail}</span>.
                                </p>
                            </div>

                            {cnpj && (
                                <div className="pt-4 border-t border-gray-100 text-xs text-gray-400">
                                    {storeName} • CNPJ: {cnpj} • Todos os direitos reservados.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default function PoliticasPage() {
    return (
        <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-[var(--page-bg,#fff8f6)]">
                <div className="animate-spin h-10 w-10 border-4 border-gray-200 border-t-[var(--primary)] rounded-full"></div>
            </div>
        }>
            <PoliticasContent />
        </Suspense>
    );
}
